import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { extractErrorMessage } from "@/core/api/errors";
import {
  PANE_INTAKE_STEPS,
  type OnboardingStepPane,
} from "@/onboarding/routes";
import { OnboardingChecklist } from "./onboarding-checklist";
import { useOnboardingState } from "./onboarding-state-context";
import { onboardingTasks } from "./onboarding-tasks";
import { useOnboardingNavigation } from "./shared-query";
import { useStepPayloadSlot } from "./step-payload-context";
import type { IntakeStep } from "./types";

/**
 * The checklist as it appears beside a task. Picking another task moves the saved
 * position, so a customer blocked on one of them can work on the rest.
 */
export const ChecklistAside = ({
  activePane,
}: {
  activePane: OnboardingStepPane;
}) => {
  const state = useOnboardingState();
  const mutation = useOnboardingNavigation(state?.business_id ?? "");
  const payload = useStepPayloadSlot();
  if (!state) return null;

  const tasks = onboardingTasks(state);
  if (!tasks.length) return null;

  const goTo = (step: IntakeStep) =>
    mutation.mutate({
      step: state.current_step,
      direction: "jump",
      to: step,
      ...(payload?.current?.() ?? {}),
    });

  return (
    <Stack className="w-full" gap={2} align="stretch">
      <OnboardingChecklist
        tasks={tasks}
        currentStep={PANE_INTAKE_STEPS[activePane] ?? state.current_step}
        onPick={goTo}
        variant="compact"
        disabled={mutation.isPending}
        onViewAll={() => goTo("welcome")}
      />
      {mutation.isError && (
        <Text variant="caption" tone="destructive">
          {extractErrorMessage(mutation.error).message}
        </Text>
      )}
    </Stack>
  );
};
